import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import TextareaAutosize from "react-textarea-autosize";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { AntDesign } from "@expo/vector-icons";

import Colors from "../../Resources/Color";
import Serie from "../Firestore/Serie";
import {
  getProtocolSuggestion,
  hasProtocolTextError,
  updateExerciseWithProtocol,
  hasSeriesWithActualData,
  handleProtocolKeyPress,
} from "../utils/protocolTextUtils";
import { useDevice } from "../_layout";
import TypingAnimation from "../Components/TypingAnimation";
import MobileScaledTextarea from "../Components/MobileScaledTextarea";
import { useTranslation } from "react-i18next";

/**
 * ProtocolTextInput - A reusable component for protocol text input with
 * autocomplete suggestions, validation, and automatic series creation.
 *
 * @param {Object} props
 * @param {Object} props.exercise - The exercise object to update
 * @param {Object} props.block - The block object (for persisting changes)
 * @param {Function} props.onShowProtocolsGuide - Callback to show protocols guide
 * @param {boolean} props.showTypingAnimation - Whether to show typing animation when empty
 * @param {string[]} props.typingAnimationTexts - Custom texts for typing animation
 * @param {Object} props.style - Additional styles for the container
 * @param {Object} props.inputStyle - Additional styles for the TextInput
 * @param {number} props.maxLength - Maximum length for the input (default 120)
 * @param {React.Ref} props.inputRef - External ref for the TextInput
 * @param {Function} props.onFocus - Callback when input is focused
 * @param {Function} props.onBlur - Callback when input loses focus
 * @param {boolean} props.autoFocus - Whether to auto-focus on mount
 */
const ProtocolTextInput = observer(
  ({
    exercise,
    block,
    onShowProtocolsGuide,
    showTypingAnimation = false,
    typingAnimationTexts,
    style,
    inputStyle,
    maxLength = 60,
    inputRef: externalRef,
    onFocus,
    onBlur,
    autoFocus = false,
  }) => {
    const { t } = useTranslation();
    const { isMobile } = useDevice();
    const [isHovered, setIsHovered] = useState(false);
    const [showingEdit, setShowingEdit] = useState(false);
    const [shouldFocusAtEnd, setShouldFocusAtEnd] = useState(false);
    const internalRef = useRef(null);
    const textInputRef = externalRef || internalRef;

    const protocolText = exercise?.protocolText ?? "";

    // Initialize edit mode based on whether protocol text is empty
    useEffect(() => {
      if (exercise) {
        setShowingEdit(protocolText === "");
      }
    }, []);

    // Auto-focus if requested
    useEffect(() => {
      if (autoFocus && textInputRef.current) {
        setTimeout(() => {
          textInputRef.current?.focus();
        }, 100);
      }
    }, [autoFocus]);

    // Perform focus when shouldFocusAtEnd is set
    useEffect(() => {
      if (showingEdit && shouldFocusAtEnd && textInputRef.current) {
        setTimeout(() => {
          textInputRef.current?.focus();
          setShouldFocusAtEnd(false);
        }, 100);
      }
    }, [showingEdit, shouldFocusAtEnd]);

    // Compute error status only when the protocol text actually changes
    const protocolTextError = useMemo(
      () => hasProtocolTextError(protocolText),
      [protocolText],
    );

    const suggestion = getProtocolSuggestion(protocolText);

    const persistBlockChange = () => {
      if (!block) return;
      if (block.client) {
        block.client.updateClientTransactionData(
          String(block.id).toLowerCase(),
        );
        block.client.startOrResetBatchTimer();
      } else if (block.coach) {
        block.coach.updateCoachTransactionBlocks(
          String(block.id).toLowerCase(),
        );
        block.coach.startOrResetBatchTimer();
      }
    };

    const sanitizeProtocolText = (text) => {
      if (text === null || text === undefined) return "";
      return String(text).replace(/[\r\n]+/g, " ");
    };

    const changeProtocol = (text) => {
      if (!exercise) return;
      const nextText = sanitizeProtocolText(text);

      // Check if any of the series have values in actual
      const actualWithData = hasSeriesWithActualData(exercise);

      if (
        actualWithData &&
        !exercise.ignoreActualWithDataWarning &&
        exercise.workout
      ) {
        exercise.setProperty("showActualWithDataWarning", true);
        return;
      }

      exercise.setProperty("protocolText", nextText);

      runInAction(() => {
        updateExerciseWithProtocol(exercise, nextText, Serie);
      });

      // Mark workout as changed and persist
      exercise?.workout?.setProperty?.("hasChanged", true);
      persistBlockChange();
    };

    const addSuggestion = (suggestionText) => {
      const newText = protocolText + suggestionText;
      exercise?.setProperty("protocolText", newText);
      runInAction(() => {
        updateExerciseWithProtocol(exercise, newText, Serie);
      });
      exercise?.workout?.setProperty?.("hasChanged", true);
      persistBlockChange();
    };

    const handleKeyPress = (e) => {
      const handled = handleProtocolKeyPress({
        event: e.nativeEvent,
        currentText: protocolText,
        onAcceptSuggestion: (newText) => {
          // Check if any of the series have values in actual
          const actualWithData = hasSeriesWithActualData(exercise);

          if (actualWithData && !exercise?.ignoreActualWithDataWarning) {
            exercise?.setProperty("showActualWithDataWarning", true);
            return;
          }

          addSuggestion(suggestion);

          if (textInputRef.current) {
            textInputRef.current.focus();
          }
        },
        onExitEdit: () => setShowingEdit(false),
        preventDefault: () => e.preventDefault(),
      });

      if (e.nativeEvent.key === "Enter") {
        e.preventDefault();
        if (!handled && protocolText !== "") {
          setShowingEdit(false);
        }
        return;
      }

      if (!handled && protocolText !== "") {
        // If key wasn't handled but we have text, exit on Tab
        if (e.nativeEvent.key === "Tab") {
          setShowingEdit(false);
          e.preventDefault();
        }
      }
    };

    const handleFocus = () => {
      onFocus?.();
    };

    const handleBlur = () => {
      if (protocolText !== "") {
        setShowingEdit(false);
      }
      onBlur?.();
    };

    const defaultTypingTexts = [
      "3x4 @5",
      "175-180-185 x4",
      "Ramp 3x4 @7 + Single @7",
      "x4 @7 + 3x4 @5",
      "x4 @4-5-6",
      "3x5x200 + Single @8",
    ];

    const animationTexts = typingAnimationTexts || defaultTypingTexts;
    const isProtocolTextEmpty = protocolText.trim() === "";

    return (
      <View
        style={[styles.container, style]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* View layer (when not editing) */}
        <View
          pointerEvents={showingEdit ? "none" : "auto"}
          style={[
            styles.viewLayer,
            {
              opacity: showingEdit ? 0 : 1,
              height: showingEdit ? 0 : undefined,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.viewTouchable}
            onPress={() => {
              setShowingEdit(true);
              setShouldFocusAtEnd(true);
            }}
            activeOpacity={0.7}
          >
            <Text
              numberOfLines={3}
              style={[styles.viewText, isHovered && styles.viewTextHovered]}
            >
              {protocolText || t("addProtocolPlaceholder-string")}
            </Text>
          </TouchableOpacity>

          {protocolTextError && (
            <AntDesign
              name="exclamationcircleo"
              size={14}
              color="red"
              style={styles.errorIcon}
              onPress={() => onShowProtocolsGuide?.()}
            />
          )}
        </View>

        {/* Edit layer */}
        <View
          style={[
            styles.editLayer,
            {
              borderColor: isHovered ? "rgba(0,0,0,0.5)" : Colors.borderColor,
              display: showingEdit ? "flex" : "none",
            },
          ]}
        >
          {/* Typing animation overlay - only visible when empty */}
          {showTypingAnimation && showingEdit && isProtocolTextEmpty && (
            <View style={styles.typingAnimationOverlay} pointerEvents="none">
              <TypingAnimation
                texts={animationTexts}
                delay={80}
                startDelay={300}
                pauseBetween={2000}
                fontSize={11}
              />
            </View>
          )}

          {/* Static display (with suggestion) behind the input */}
          <Text style={styles.suggestionText}>
            {protocolText}
            {suggestion && (
              <Text style={styles.suggestionHint}>{suggestion}</Text>
            )}
          </Text>

          <TextInput
            ref={textInputRef}
            editable={showingEdit}
            style={[
              StyleSheet.absoluteFillObject,
              styles.textInput,
              inputStyle,
            ]}
            value={protocolText}
            selection={
              showingEdit && shouldFocusAtEnd
                ? {
                    start: protocolText.length,
                    end: protocolText.length,
                  }
                : undefined
            }
            onChangeText={changeProtocol}
            autoCorrect={false}
            autoCapitalize="none"
            placeholderTextColor={Colors.grayColor}
            multiline
            onKeyPress={handleKeyPress}
            blurOnSubmit={false}
            maxLength={maxLength}
            onFocus={handleFocus}
            onBlur={handleBlur}
            scrollEnabled={false}
          />
        </View>
      </View>
    );
  },
);

/**
 * InlineProtocolTextInput - A simpler inline version for use in forms/cards
 * that doesn't toggle between view/edit modes.
 */
export const InlineProtocolTextInput = observer(
  ({
    exercise,
    block,
    onShowProtocolsGuide,
    showTypingAnimation = false,
    typingAnimationTexts,
    style,
    inputStyle,
    maxLength = 60,
    inputRef: externalRef,
    onFocus,
    onBlur,
    placeholder,
  }) => {
    const { t } = useTranslation();
    const { isMobile, isIOS } = useDevice();
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const internalRef = useRef(null);
    const textInputRef = externalRef || internalRef;

    const protocolText = exercise?.protocolText ?? "";

    const suggestion = getProtocolSuggestion(protocolText);

    const persistBlockChange = () => {
      if (!block) return;
      if (block.client) {
        block.client.updateClientTransactionData(
          String(block.id).toLowerCase(),
        );
        block.client.startOrResetBatchTimer();
      } else if (block.coach) {
        block.coach.updateCoachTransactionBlocks(
          String(block.id).toLowerCase(),
        );
        block.coach.startOrResetBatchTimer();
      }
    };

    const sanitizeProtocolText = (text) => {
      if (text === null || text === undefined) return "";
      return String(text).replace(/[\r\n]+/g, " ");
    };

    const changeProtocol = (text) => {
      if (!exercise) return;
      const nextText = sanitizeProtocolText(text);

      // Check if any of the series have values in actual
      const actualWithData = hasSeriesWithActualData(exercise);

      if (
        actualWithData &&
        !exercise.ignoreActualWithDataWarning &&
        exercise.workout
      ) {
        exercise.setProperty("showActualWithDataWarning", true);
        return;
      }

      exercise.setProperty("protocolText", nextText);

      runInAction(() => {
        updateExerciseWithProtocol(exercise, nextText, Serie);
      });

      // Mark workout as changed and persist
      exercise?.workout?.setProperty?.("hasChanged", true);
      persistBlockChange();
    };

    const handleKeyPress = (e) => {
      const currentSuggestion = getProtocolSuggestion(protocolText);
      // TextareaAutosize uses standard DOM events, not React Native events
      const keyEvent = {
        key: e.key,
        preventDefault: () => e.preventDefault(),
      };

      const handled = handleProtocolKeyPress({
        event: keyEvent,
        currentText: protocolText,
        onAcceptSuggestion: (newText) => {
          // Check if any of the series have values in actual
          const actualWithData = hasSeriesWithActualData(exercise);

          if (actualWithData && !exercise?.ignoreActualWithDataWarning) {
            exercise?.setProperty("showActualWithDataWarning", true);
            return;
          }

          // Apply the suggestion
          exercise?.setProperty("protocolText", newText);
          runInAction(() => {
            updateExerciseWithProtocol(exercise, newText, Serie);
          });
          exercise?.workout?.setProperty?.("hasChanged", true);
          persistBlockChange();

          if (textInputRef.current) {
            textInputRef.current.focus();
          }
        },
        onExitEdit: () => {
          textInputRef.current?.blur();
        },
        preventDefault: () => e.preventDefault(),
      });

      if (e.key === "Enter") {
        e.preventDefault();
        return;
      }

      if (!handled && protocolText !== "") {
        if (e.key === "Tab") {
          e.preventDefault();
        }
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      onFocus?.();
    };

    const handleBlur = () => {
      setIsFocused(false);
      onBlur?.();
    };

    const defaultTypingTexts = [
      "3x4 @5",
      "175-180-185 x4",
      "Ramp 3x4 @7 + Single @7",
      "x4 @7 + 3x4 @5",
      "x4 @4-5-6",
      "3x5x200 + Single @8",
    ];

    const animationTexts = typingAnimationTexts || defaultTypingTexts;
    const isProtocolTextEmpty = protocolText.trim() === "";
    const shouldShowTypingHelper = showTypingAnimation && isFocused;

    return (
      <View
        style={[styles.inlineContainer, style]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View style={styles.inlineInputWrapper}>
          <MobileScaledTextarea
            ref={(node) => {
              if (textInputRef) {
                if (typeof textInputRef === "function") {
                  textInputRef(node);
                } else if (textInputRef.current !== undefined) {
                  textInputRef.current = node;
                }
              }
            }}
            value={protocolText}
            onChange={(e) => changeProtocol(e.target.value)}
            placeholder={
              shouldShowTypingHelper
                ? ""
                : placeholder || t("addProtocolPlaceholder-string")
            }
            fontSize={isMobile ? 13 : 12}
            style={{
              width: "100%",
              resize: "none",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              border: "none",
              outline: "none",
              fontWeight: "500",
              color: Colors.darkgrayColor,
              backgroundColor: "transparent",
              padding: 0,
              margin: 0,
              textAlign: "left",
              lineHeight: "normal",
              overflow: "hidden",
            }}
            className="inline-protocol-textarea"
            minRows={1}
            maxRows={2}
            maxLength={maxLength}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyPress}
            autoCorrect="off"
            spellCheck="false"
          />

          {/* Suggestion overlay */}
          {isFocused && suggestion && (
            <View
              style={[
                styles.inlineSuggestionOverlay,
                // iOS mobile: Match the MobileScaledTextarea transform exactly
                // Textarea uses: transform: scale(13/16) = 0.8125 with transformOrigin: "center left"
                // Width compensation: 100 * (16/13) = 123.08% to compensate for scale
                isMobile &&
                  isIOS && {
                    // Use CSS transform string to match textarea exactly
                    transform: `scale(${13 / 16}) translateZ(0)`, // Match textarea's transform exactly
                    transformOrigin: "center left", // Match textarea's transformOrigin exactly
                    width: `${100 * (16 / 13)}%`, // Compensate for scale (123.08%)
                    willChange: "transform",
                    // Ensure overlay aligns with textarea content by matching padding/margin
                    padding: 0,
                    margin: 0,
                  },
              ]}
              pointerEvents="none"
            >
              <Text
                style={[
                  styles.inlineSuggestionText,
                  // iOS mobile: use 16px font to match the scaled textarea's base font size
                  // The textarea uses fontSize: 16px which gets scaled to 13px visually
                  isMobile &&
                    isIOS && {
                      fontSize: 16, // Match textarea's base font size
                      lineHeight: "normal", // Match textarea's lineHeight for consistent rendering
                      // Ensure text aligns exactly with textarea text
                      padding: 0,
                      margin: 0,
                    },
                  // Non-iOS mobile: use 13px to match textarea's fontSize
                  isMobile &&
                    !isIOS && {
                      fontSize: 13, // +1px for non-iOS mobile
                    },
                ]}
              >
                <Text style={{ color: "transparent" }}>{protocolText}</Text>
                <Text style={styles.suggestionHint}>{suggestion}</Text>
              </Text>
            </View>
          )}

          {/* Typing animation overlay */}
          {shouldShowTypingHelper && isProtocolTextEmpty && (
            <View style={styles.inlineTypingOverlay} pointerEvents="none">
              <TypingAnimation
                texts={animationTexts}
                delay={80}
                startDelay={300}
                pauseBetween={2000}
                fontSize={isMobile ? 13 : 12}
              />
            </View>
          )}
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  viewLayer: {
    position: "relative",
    width: "100%",
    padding: 2,
    minHeight: 32,
    justifyContent: "center",
  },
  viewTouchable: {
    width: "100%",
  },
  viewText: {
    width: "100%",
    color: "gray",
    textAlign: "center",
    fontSize: 10,
    padding: 2,
  },
  viewTextHovered: {
    fontWeight: "600",
  },
  errorIcon: {
    position: "absolute",
    top: -8,
    right: -8,
    padding: 2,
    opacity: 0.7,
  },
  editLayer: {
    position: "relative",
    width: "100%",
    borderWidth: 0.5,
    borderRadius: 8,
    minHeight: 32,
    padding: 4,
  },
  typingAnimationOverlay: {
    position: "absolute",
    top: 4,
    left: 4,
    right: 4,
    bottom: 4,
    justifyContent: "flex-start",
    alignItems: "flex-start",
    pointerEvents: "none",
    zIndex: 0,
  },
  suggestionText: {
    fontSize: 11,
    color: "transparent",
    flexWrap: "wrap",
    marginBottom: 2,
  },
  suggestionHint: {
    color: "grey",
  },
  textInput: {
    top: 4,
    left: 4,
    right: 4,
    bottom: 4,
    color: "black",
    fontSize: 11,
    textAlignVertical: "top",
    outline: "none",
    backgroundColor: "transparent",
    zIndex: 1,
  },
  // Inline styles
  inlineContainer: {
    flex: 1,
    position: "relative",
  },
  inlineInputWrapper: {
    flex: 1,
    position: "relative",
  },
  inlineTextInput: {
    // Base styles - most styling is done inline to match TextareaAutosize requirements
    // Additional styles can be passed via inputStyle prop
  },
  inlineSuggestionOverlay: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0, // Match textarea container height to ensure alignment when wrapping
    justifyContent: "flex-start", // Align content from top to prevent shifting when text wraps
    alignItems: "flex-start", // Align text to the left
    // On iOS, the overlay needs to match textarea's content positioning exactly
    // borderWidth: 1, // Uncomment to debug positioning
  },
  inlineSuggestionText: {
    fontSize: 12,
    fontWeight: "500",
    // lineHeight: "normal",
  },
  inlineTypingOverlay: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
  },
});

export default ProtocolTextInput;
